// Shared chrome for the standalone eWings pages (Analytics, Reminders, Inbox,
// Documents).
//
// These pages render inside Twenty's shell, immediately beside record tables
// and settings screens. Earlier versions used a gradient hero, 12px radii and
// a large display typeface, which made them read as a different product
// bolted on. Everything here now uses the shell's own tokens (shell-tokens.ts,
// extracted from Twenty's stylesheet) so the pages match structurally.
//
// Brand identity still comes through in accent colour and copy -- what changed
// is the shape, weight and density, not the palette.
import type { ReactNode } from 'react';

import {
  getShellColors,
  SHELL_FONT,
  SHELL_FONT_FAMILY,
  SHELL_RADIUS,
  SHELL_SPACE,
  SHELL_TYPE,
  SHELL_WEIGHT,
  v,
} from 'src/front-components/theme/shell-tokens';
import { type Palette } from 'src/front-components/theme/ewings-theme';

/**
 * Page header in the shell's idiom: a title line with a muted subtitle and an
 * optional action slot, separated by a hairline rule. The shell already puts
 * the page name and icon in the card header above, so the `eyebrow` prop is
 * accepted but not drawn -- repeating the name in accent colour read as a
 * second, foreign header.
 */
export const PageMasthead = ({
  palette: _palette,
  scheme,
  eyebrow: _eyebrow,
  title,
  subtitle,
  actions,
}: {
  palette: Palette;
  scheme: string;
  eyebrow?: string;
  title: string;
  subtitle: string;
  actions?: ReactNode;
}) => {
  const shell = getShellColors(scheme);

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: SHELL_SPACE[3],
        paddingBottom: SHELL_SPACE[4],
        marginBottom: SHELL_SPACE[5],
        borderBottom: `1px solid ${shell.borderMedium}`,
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ flex: '1 1 240px', minWidth: 0 }}>
        <div style={{ ...SHELL_TYPE.pageTitle, color: shell.text }}>{title}</div>
        <div
          style={{
            ...SHELL_TYPE.body,
            color: shell.textSecondary,
            marginTop: SHELL_SPACE[1],
          }}
        >
          {subtitle}
        </div>
      </div>

      {actions !== undefined && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: SHELL_SPACE[2], alignItems: 'center', maxWidth: '100%' }}>
          {actions}
        </div>
      )}
    </div>
  );
};

/**
 * Underlined tab strip, matching the record page's tab treatment rather than
 * the filled pills used before.
 */
export const TabStrip = ({
  palette: _palette,
  scheme,
  tabs,
  active,
  onSelect,
}: {
  palette: Palette;
  scheme: string;
  tabs: ReadonlyArray<{ id: string; label: string; count?: number }>;
  active: string;
  onSelect: (id: string) => void;
}) => {
  const shell = getShellColors(scheme);

  return (
    <div
      style={{
        display: 'flex',
        gap: SHELL_SPACE[4],
        marginBottom: SHELL_SPACE[5],
        borderBottom: `1px solid ${shell.borderMedium}`,
        flexWrap: 'wrap',
      }}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === active;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onSelect(tab.id)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: SHELL_SPACE[1.5],
              padding: `0 0 ${SHELL_SPACE[2]} 0`,
              border: 'none',
              borderBottom: `1px solid ${isActive ? shell.text : 'transparent'}`,
              background: 'transparent',
              color: isActive ? shell.text : shell.textSecondary,
              ...SHELL_TYPE.label,
              cursor: 'pointer',
              marginBottom: '-1px',
            }}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span
                style={{
                  ...SHELL_TYPE.caption,
                  padding: `0 ${SHELL_SPACE[1.5]}`,
                  borderRadius: SHELL_RADIUS.sm,
                  background: shell.backgroundQuaternary,
                  color: shell.textSecondary,
                }}
              >
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

/** Card matching the shell's surface treatment: 8px radius, hairline border. */
export const ShellCard = ({
  scheme,
  children,
  style,
  padded = true,
}: {
  scheme: string;
  children: ReactNode;
  style?: React.CSSProperties;
  padded?: boolean;
}) => {
  const shell = getShellColors(scheme);

  return (
    <div
      style={{
        background: shell.background,
        border: `1px solid ${shell.borderMedium}`,
        borderRadius: SHELL_RADIUS.md,
        padding: padded ? SHELL_SPACE[4] : 0,
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
        ...style,
      }}
    >
      {children}
    </div>
  );
};

/** Section heading inside a card. */
export const ShellSectionLabel = ({
  scheme,
  children,
  action,
}: {
  scheme: string;
  children: ReactNode;
  action?: ReactNode;
}) => {
  const shell = getShellColors(scheme);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: SHELL_SPACE[2],
        marginBottom: SHELL_SPACE[3],
      }}
    >
      <span style={{ ...SHELL_TYPE.sectionTitle, color: shell.text }}>{children}</span>
      {action}
    </div>
  );
};

/** Compact stat tile. Uses the shell's metric size, not a hero number. */
export const ShellStat = ({
  scheme,
  label,
  value,
  hint,
  accent: _accent,
}: {
  scheme: string;
  label: string;
  value: string | number;
  hint?: string;
  /** Accepted for call-site compatibility; the tile no longer draws it. */
  accent?: string;
}) => {
  const shell = getShellColors(scheme);

  return (
    <div
      style={{
        background: shell.background,
        border: `1px solid ${shell.borderMedium}`,
        borderRadius: SHELL_RADIUS.md,
        padding: SHELL_SPACE[4],
      }}
    >
      <div style={{ ...SHELL_TYPE.label, color: shell.textSecondary }}>{label}</div>
      <div style={{ ...SHELL_TYPE.metric, color: shell.text, marginTop: SHELL_SPACE[1] }}>
        {value}
      </div>
      {hint !== undefined && (
        <div style={{ ...SHELL_TYPE.caption, color: shell.textTertiary, marginTop: SHELL_SPACE[1] }}>
          {hint}
        </div>
      )}
    </div>
  );
};

/**
 * Twenty theme colour names accepted by the shell's Tag component.
 * Anything else falls back to gray.
 */
export type TagColor =
  | 'gray'
  | 'red'
  | 'orange'
  | 'amber'
  | 'yellow'
  | 'green'
  | 'turquoise'
  | 'sky'
  | 'blue'
  | 'purple'
  | 'pink';

/**
 * Status chip drawn exactly like twenty-ui's Tag: the shell's
 * --t-tag-background-* / --t-tag-text-* pair, no border, 20px tall.
 * `color` is a theme colour name, not a hex value.
 */
export const ShellChip = ({ label, color = 'gray' }: { label: string; color?: TagColor | string }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      boxSizing: 'content-box',
      height: SHELL_SPACE[5],
      padding: `0 ${SHELL_SPACE[2]}`,
      borderRadius: v('border-radius-sm-round'),
      background: v(`tag-background-${color}`),
      color: v(`tag-text-${color}`),
      fontSize: SHELL_FONT.md,
      fontWeight: SHELL_WEIGHT.regular,
      whiteSpace: 'nowrap',
    }}
  >
    {label}
  </span>
);

export const ShellEmptyState = ({
  scheme,
  message,
  hint,
}: {
  scheme: string;
  message: string;
  hint?: string;
}) => {
  const shell = getShellColors(scheme);

  return (
    <div
      style={{
        padding: `${SHELL_SPACE[8]} ${SHELL_SPACE[4]}`,
        textAlign: 'center',
      }}
    >
      <div style={{ ...SHELL_TYPE.bodyMedium, color: shell.textSecondary }}>{message}</div>
      {hint !== undefined && (
        <div style={{ ...SHELL_TYPE.caption, color: shell.textTertiary, marginTop: SHELL_SPACE[1] }}>
          {hint}
        </div>
      )}
    </div>
  );
};

/**
 * Banner for a screen whose backing data model does not exist yet.
 * Deliberately quiet -- it is an honest disclosure, not a promotion.
 */
export const PreviewNotice = ({
  palette: _palette,
  scheme,
  needs,
}: {
  palette: Palette;
  scheme: string;
  needs: string;
}) => {
  const shell = getShellColors(scheme);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: SHELL_SPACE[2],
        padding: `${SHELL_SPACE[2]} ${SHELL_SPACE[3]}`,
        marginBottom: SHELL_SPACE[4],
        borderRadius: SHELL_RADIUS.md,
        background: shell.backgroundTertiary,
        border: `1px solid ${shell.borderMedium}`,
      }}
    >
      <span style={{ ...SHELL_TYPE.body, color: shell.textSecondary }}>
        <strong style={{ color: shell.text, fontWeight: 500 }}>Layout preview.</strong> Sample
        rows below. Live data needs {needs}.
      </span>
    </div>
  );
};

/**
 * Page container. The standalone page already sits on the shell's primary
 * card surface, so this paints nothing of its own -- a second, darker fill
 * here is what made the pages look inset. Font family and colour inherit
 * the shell's variables explicitly, since the front-component root does not.
 */
export const PageBody = ({
  scheme,
  children,
}: {
  scheme: string;
  children: ReactNode;
}) => {
  const shell = getShellColors(scheme);

  return (
    <div
      style={{
        padding: 'var(--page-body-pad, var(--t-spacing-6, 24px))',
        color: shell.text,
        minHeight: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        fontFamily: SHELL_FONT_FAMILY,
        fontSize: SHELL_TYPE.body.fontSize,
      }}
    >
      {children}
    </div>
  );
};

/**
 * Responsive grid that collapses cleanly instead of forcing a horizontal
 * scrollbar. `min` is the narrowest a column may get before wrapping.
 */
export const ShellGrid = ({
  min = 260,
  children,
}: {
  min?: number;
  children: ReactNode;
}) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fit, minmax(min(${min}px, 100%), 1fr))`,
      gap: SHELL_SPACE[3],
      alignItems: 'start',
      width: '100%',
      minWidth: 0,
      maxWidth: '100%',
      boxSizing: 'border-box',
    }}
  >
    {children}
  </div>
);
